
import React, { createContext, useContext, useEffect, useState } from 'react';
import { analytics } from './analytics';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

// Create context
type AnalyticsContextType = {
  trackEvent: (name: string, params?: Record<string, any>) => void;
  trackPageView: (path: string, title?: string) => void;
  trackButtonClick: (buttonName: string, params?: Record<string, any>) => void;
};

// Export the context so it can be imported elsewhere
export const AnalyticsContext = createContext<AnalyticsContextType>({
  trackEvent: () => {},
  trackPageView: () => {},
  trackButtonClick: () => {},
});

// Analytics provider component
export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  // Initialize analytics
  useEffect(() => {
    const init = async () => {
      await analytics.initialize();
      setIsInitialized(true);
    };
    init();
  }, []);

  // Track page views
  useEffect(() => {
    if (!isInitialized) return;
    
    analytics.trackPageView(location.pathname);
  }, [location, isInitialized]);

  // Identify user when auth state changes
  useEffect(() => {
    if (!isInitialized) return;
    
    if (user) {
      analytics.identify(user.uid, {
        email: user.email,
        displayName: user.displayName,
      });
    } else {
      analytics.reset();
    }
  }, [user, isInitialized]);

  // Context value
  const contextValue = {
    trackEvent: (name: string, params?: Record<string, any>) => {
      analytics.trackEvent(name, params);
    },
    trackPageView: (path: string, title?: string) => {
      analytics.trackPageView(path, title);
    },
    trackButtonClick: (buttonName: string, params?: Record<string, any>) => {
      analytics.trackButtonClick(buttonName, params);
    },
  };

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};

// Hook for using analytics in components
export const useAnalytics = () => useContext(AnalyticsContext);
